import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';

import { getDeployedEnvType, useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { Layout, respGridUtil, prePageCall, getProcessEnvs } from '@marriott/mi-headless-utils';
import { constants, MIN_PAGE_HEIGHT, backgroundColorMapping, PageParamsContext } from '@marriott/mi-awp-components';
import { GetServerSideProps } from 'next';

import operationSignatures from '../../_constants/operationSignatures.json';
import { useMemo } from 'react';

const { NEXT_PUBLIC_AEM_PATH } = process.env;
const { ENV_DEV } = process.env;
const isDev = process.env['NODE_ENV'] === ENV_DEV;
const { ITEM_PATH_ROOT, ITEM_PATH_FOOTER, ITEM_PATH_HEADER } = constants;

const mockModel = async () => {
  if (isDev) {
    // nx-ignore-next-line
    const { awpErrorPage404 } = await import('@marriott/mi-awp-components/localModel');
    return awpErrorPage404;
  }
  return {};
};

// final data would have to come from 2 calls:
// 1. Page level call from AEM - that will populate the labels on components
// 2. Functional data for components like list of amenities and other related information
// import jsonContent from '../../mock/label-info.json';
export default function AwpErrorPage({ model, deployedEnvType, isAuthorMode, currentLocale }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const MiApolloClient = useNextMiApolloClient(operationSignatures, deployedEnvType);
  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const responsiveGridHeaderModel = respGridUtil(model?.cqItems?.root?.[':items']?.['header-responsivegrid']);
  const responsiveGridFooterModel = respGridUtil(model?.cqItems?.root?.[':items']?.['footer-responsivegrid']);

  const {
    metaNames,
    metaProperties,
    canonical,
    title,
    staticComponentClientLibsJS,
    staticComponentClientLibsCSS,
    gridCss,
    hrefLangList,
    bgColor,
  } = model;

  const bgColorSearch = backgroundColorMapping[bgColor];

  const contextValue = useMemo(() => {
    return {
      currentLocale,
    };
  }, [currentLocale]);

  return (
    <ApolloProvider client={MiApolloClient}>
      <PageParamsContext.Provider value={contextValue}>
        <Layout
          metaNames={metaNames}
          metaProps={metaProperties}
          title={title}
          staticComponentClientLibsCSS={staticComponentClientLibsCSS}
          staticComponentClientLibsJS={staticComponentClientLibsJS}
          isAuthorMode={isAuthorMode}
          gridCss={gridCss}
          canonical={canonical}
          hreflangList={hrefLangList}
        >
          <section>
            {responsiveGridHeaderModel && (
              <ResponsiveGrid
                {...responsiveGridHeaderModel}
                model={responsiveGridHeaderModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_HEADER}
              />
            )}
            <div style={{ background: bgColorSearch, minHeight: MIN_PAGE_HEIGHT }}>
              <ResponsiveGrid
                {...responsiveGridModel}
                model={responsiveGridModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_ROOT}
              />
            </div>
            {responsiveGridFooterModel && (
              <ResponsiveGrid
                {...responsiveGridFooterModel}
                model={responsiveGridFooterModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_FOOTER}
              />
            )}
          </section>
        </Layout>
      </PageParamsContext.Provider>
    </ApolloProvider>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const awpSpecificData = async () => {
  //any page specific code can be written here.
  const deployedEnvType = getDeployedEnvType();
  const { GOOGLE_MAP_API_KEY } = getProcessEnvs();

  return {
    operationSignatures: operationSignatures,
    deployedEnvType,
    GOOGLE_MAP_API_KEY,
  };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServerSideProps: GetServerSideProps<any> = prePageCall(awpSpecificData, mockModel, false, true, true);
